import { useCallback, useEffect, useRef, useState } from 'react';
import type { KickoffSlide, PhaseId, PhaseStatus, StakeholderRole } from '@wedpo/soc2';
import { STAKEHOLDER_ORDER, kickoff, roleShortLabels } from '@wedpo/soc2';

const roleBlurb: Record<StakeholderRole, string> = {
  management: 'Owns scope, sign-off, and the engagement overall.',
  security: 'Owns policies, the gap analysis, and evidence quality.',
  engineering: 'Owns the system boundary and technical controls.',
  it: 'Owns access, identity, and auditor provisioning.',
  legal: 'Owns privacy, contracts, and data commitments.',
  risk: 'Owns the risk assessment and exceptions log.',
  hr: 'Owns onboarding, training, and the people controls.',
};

/** Osh presenter: plays /osh.mp4 if present, else falls back to the poster art. */
function OshStage() {
  const [failed, setFailed] = useState(false);
  return (
    <figure className="deck-osh">
      <div className="osh-frame">
        {failed ? (
          <img className="osh-art" src="/osh-poster.svg" alt="Osh, your SOC 2 guide" />
        ) : (
          <video
            className="osh-art"
            src="/osh.mp4"
            poster="/osh-poster.svg"
            autoPlay
            muted
            loop
            playsInline
            onError={() => setFailed(true)}
          />
        )}
        <span className="osh-badge">Osh</span>
      </div>
      <figcaption className="osh-cap">
        <span className="osh-dot" /> Your SOC 2 guide
      </figcaption>
    </figure>
  );
}

function MiniTimeline({ phaseStatus }: { phaseStatus: Record<PhaseId, PhaseStatus> }) {
  return (
    <div className="deck-timeline">
      {kickoff.phases.map((ph) => {
        const st = phaseStatus[ph.id];
        return (
          <div className="dt-seg" key={ph.id} style={{ flexGrow: ph.weeks }}>
            <div className={`dt-node dt-${st}`}>{ph.n}</div>
            <div className="dt-label">{ph.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function RolesGrid() {
  return (
    <div className="deck-roles">
      {STAKEHOLDER_ORDER.map((role) => (
        <div className="dr-row" key={role}>
          <span className={`owdot ow-${role}`} />
          <div>
            <div className="dr-name">{roleShortLabels[role]}</div>
            <div className="dr-blurb">{roleBlurb[role]}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SlideBody({
  slide,
  phaseStatus,
}: {
  slide: KickoffSlide;
  phaseStatus: Record<PhaseId, PhaseStatus>;
}) {
  const kind = slide.kind ?? 'content';
  return (
    <div className={`deck-slide slide-${kind}`}>
      {slide.kicker && <p className="ds-kicker">{slide.kicker}</p>}
      <h2 className="ds-title">{slide.title}</h2>
      {slide.lead && <p className="ds-lead">{slide.lead}</p>}

      {kind === 'timeline' && <MiniTimeline phaseStatus={phaseStatus} />}
      {kind === 'roles' && <RolesGrid />}

      {slide.bullets && (
        <ul className="ds-bullets">
          {slide.bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      )}
      {slide.note && <p className="ds-note">{slide.note}</p>}
    </div>
  );
}

export function KickoffDeck({
  phaseStatus,
}: {
  phaseStatus: Record<PhaseId, PhaseStatus>;
}) {
  const deck = kickoff.deck;
  const [i, setI] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);

  const go = useCallback(
    (next: number) => setI(Math.max(0, Math.min(deck.length - 1, next))),
    [deck.length],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(i + 1);
      else if (e.key === 'ArrowLeft') go(i - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [i, go]);

  const present = () => {
    const el = stageRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => {});
  };

  return (
    <div className="deck">
      <div className="deck-stage" ref={stageRef}>
        <SlideBody slide={deck[i]} phaseStatus={phaseStatus} />
        <OshStage />
        <span className="deck-pageno">
          {i + 1} / {deck.length}
        </span>
      </div>

      <div className="deck-controls">
        <button className="btn btn-ghost" onClick={() => go(i - 1)} disabled={i === 0}>
          ← Prev
        </button>
        <div className="deck-dots">
          {deck.map((s, d) => (
            <button
              key={s.id}
              className={`deck-dot${d === i ? ' on' : ''}`}
              aria-label={`Go to slide ${d + 1}`}
              onClick={() => go(d)}
            />
          ))}
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => go(i + 1)}
          disabled={i === deck.length - 1}
        >
          Next →
        </button>
        <div className="deck-spacer" />
        <button className="btn btn-ghost" onClick={present}>
          Present
        </button>
        <button className="btn btn-dark" onClick={() => window.print()}>
          Download / print
        </button>
      </div>

      {/* Print-only: every slide stacked for a clean PDF export. */}
      <div className="deck-print">
        {deck.map((s) => (
          <div className="deck-print-slide" key={s.id}>
            <SlideBody slide={s} phaseStatus={phaseStatus} />
          </div>
        ))}
      </div>
    </div>
  );
}
